import { FormEventHandler, ReactNode, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useForm } from 'react-hook-form';
import dayjs, { Dayjs } from 'dayjs';
import { capitalize } from '@mui/material/utils';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import { useLocationStore } from '@libs/store/locations';
import { useAssignStore } from '@libs/store/talents';
import { getShiftType } from '@libs/models/shifts/shift';
import { FormAutocomplete, Form } from '@molecules/form';
import { Box, Flex, PrimaryDialog, PrimaryDialogProps } from '@atoms/layout';
import { LoadingButton, Button } from '@atoms/buttons';
import { FaIcon } from '@atoms/icons';

type AssignDialogProps = Omit<PrimaryDialogProps, 'onSubmit'> & {
  shift: {
    shiftStart?: Dayjs;
    shiftLength?: number;
    qualifications?: { id: string; abbreviation: string }[];
  };
  onSubmit?: (talent: string) => void;
  loading?: boolean;
  warningMessage?: ReactNode;
};
export function AssignDialog({ shift, onSubmit, loading, warningMessage, ...props }: AssignDialogProps) {
  return (
    <PrimaryDialog fullWidth maxWidth={'xs'} {...props}>
      <Content
        shift={shift}
        onCloseClick={() => props.onClose?.({}, 'escapeKeyDown')}
        onSubmit={onSubmit}
        loading={loading}
        warningMessage={warningMessage}
      />
    </PrimaryDialog>
  );
}

type ContentProps = {
  shift: {
    shiftStart?: Dayjs;
    shiftLength?: number;
    qualifications?: { id: string; abbreviation: string }[];
  };
  onCloseClick: () => void;
  onSubmit?: (talent: string) => void;
  loading?: boolean;
  warningMessage?: ReactNode;
};
function Content({ shift, onCloseClick, onSubmit, loading = false, warningMessage }: ContentProps) {
  const intl = useIntl();
  const shiftType = getShiftType(dayjs(shift?.shiftStart));
  const to = dayjs(shift?.shiftStart).add(shift?.shiftLength ?? 0, 'millisecond');
  const data = useAssignStore((s) => s.data);
  const loadData = useAssignStore((s) => s.loadData);
  const { currentWorkingLocationId } = useLocationStore();

  const talentsOptions = data.map((item) => ({
    value: item.id,
    label: item.fullName,
    qualifications: item.qualifications,
  }));

  const methods = useForm<AssignForm>({
    defaultValues: {
      talent: '',
    },
  });

  const [assigneeValue, setAssigneeValue] = useState<string>('');

  useEffect(() => {
    if (!currentWorkingLocationId) {
      return;
    }
    loadData({
      search: assigneeValue,
      location: currentWorkingLocationId,
      qualifications: shift.qualifications?.map((q) => q.id),
    });
  }, [assigneeValue, currentWorkingLocationId, loadData, shift.qualifications]);

  const handleAssigneeChange: FormEventHandler<HTMLDivElement> = (evt) => {
    const target = evt.target as HTMLInputElement;
    setAssigneeValue(target.value);
  };

  return (
    <>
      <Form formUtils={methods} onSubmit={(v) => onSubmit?.(v.talent)}>
        <DialogTitle sx={{ px: 3, pt: 4, pb: 2 }}>
          <Flex justifyContent="space-between">
            <Flex column gap={1}>
              <Typography variant="h5">
                <FormattedMessage id="shifts.assignDialog.title" />
              </Typography>
            </Flex>
            <Box>
              <IconButton sx={{ width: '40px', position: 'relative', top: -5, right: -5 }} onClick={onCloseClick}>
                <FaIcon name="xmark" variant="light" />
              </IconButton>
            </Box>
          </Flex>
        </DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1">
            <FormattedMessage
              id="shifts.assignDialog.shiftDetails"
              values={{
                period: `${intl.formatTime(shift?.shiftStart?.toDate(), { hour: 'numeric' })} - ${intl.formatTime(to.toDate(), { hour: 'numeric' })}`,
                length: intl.formatMessage(
                  { id: 'shifts.length' },
                  {
                    length: Math.round((shift?.shiftLength ?? 0) / 1000 / 60 / 60),
                  },
                ),
                type: capitalize(shiftType),
                qualifications: shift?.qualifications?.map((qual) => qual.abbreviation).join(', '),
              }}
            />
          </Typography>
          <Flex mt={2}>
            <FormAutocomplete
              name="talent"
              fullWidth
              async
              options={talentsOptions}
              placeholder={intl.formatMessage({ id: 'shifts.assignDialog.placeholder' })}
              required
              onInput={handleAssigneeChange}
              renderOption={(props, option) => (
                <li {...props} key={option.value}>
                  <Flex justifyContent="space-between" alignCenter width="100%">
                    <Typography>{option.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(option.qualifications || []).map((q) => q.abbreviation).join(', ')}
                    </Typography>
                  </Flex>
                </li>
              )}
            />
          </Flex>

          {warningMessage && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {warningMessage}
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 0 }}>
          <Box color="text.secondary">
            <Button variant="text" color="inherit" rounded onClick={onCloseClick} size="large">
              <FormattedMessage id="common.cancel" />
            </Button>
          </Box>
          <LoadingButton loading={loading} variant="contained" rounded type="submit" size="large">
            <FormattedMessage id="common.submit" />
          </LoadingButton>
        </DialogActions>
      </Form>
    </>
  );
}

type AssignForm = {
  talent: string;
};
